import React, { useMemo } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { FormProvider, useForm } from "react-hook-form";
import { v4 as uuidv4 } from "uuid";

import { PaziaFormButton } from "components/atoms";
import { formatDate } from "utils";

import { CourseSessionApi } from "api";

import { TabContent, TabGrid, TabHeader } from "../TabLayout";
import { DeleteButton, EditButton } from "components/atoms/Buttons";
import { queryClient } from "main";
import { QUERIES_KEYS } from "queries";
import { EditSessionModal } from "components/templates";
import { ConfirmationModal, ToggleReadinessButton } from "components/molecules";
import { coursesStatusApi } from "api/coursesStatusApi";
import { PaziaDateRangeSelect } from "components/atoms";
import { PaziaBox } from "components/atoms";

interface SessionsFormProps {
  courseId: number;
  onSubmit: (data: SessionFormValues) => Promise<void>;
  deleteSession: (sessionId: number) => Promise<void>;
  sessions: CourseSessionApi[];
  refreshSessions: () => Promise<void>;
  isReady: boolean;
}

export const SessionsForm = ({
  courseId,
  onSubmit,
  deleteSession,
  isReady,
  sessions,
  refreshSessions,
}: SessionsFormProps): JSX.Element => {
  const [editSession, setEditSession] = React.useState<CourseSessionApi | undefined>(undefined);
  const [sessionToDelete, setSessionToDelete] = React.useState<CourseSessionApi | undefined>(undefined);
  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      [SESSIONS_FORM_NAME.SESSIONS]: sessions,
    },
  });
  const { handleSubmit, reset } = methods;

  const { watch } = methods;
  const sessionFrom = watch(SESSIONS_FORM_NAME.SESSION_FROM);

  const onSubmitHandler = async (data: SessionFormValues) => {
    await onSubmit(data);
    reset({ sessions: [] });
    queryClient.invalidateQueries({ queryKey: QUERIES_KEYS.COURSE_BY_ID(courseId) });
  };

  const range = useMemo(() => {
    const start = new Date(2020, 0, 1);
    const fourYearsAhead = new Date();
    fourYearsAhead.setFullYear(fourYearsAhead.getFullYear() + 4);
    return [start, fourYearsAhead] as [Date, Date];
  }, []);

  return (
    <>
      <FormProvider {...methods}>
        <TabContent>
          <TabGrid>
            <PaziaBox className="flex justify-between lg:col-span-2">
              <div>
                <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100">Zjazdy</h2>
                <div className="mb-2 text-sm">Dodaj zjazdy, które będą odbywać się w ramach kursu.</div>
                <ToggleReadinessButton
                  isReady={isReady}
                  onReady={coursesStatusApi.SESSIONS_READY}
                  onUnready={coursesStatusApi.SESSIONS_UNREADY}
                  courseId={courseId}
                  readyText="Zjazdy zostały zatwierdzone"
                  invalidate={refreshSessions}
                />
              </div>
              <div>
                <PaziaFormButton title="Zapisz" type="success" />
              </div>
            </PaziaBox>
            <PaziaBox header="Dodawanie nowego zjazdu">
              <form onSubmit={handleSubmit(onSubmitHandler)}>
                <div className="flex flex-col justify-between gap-2 md:flex-row">
                  <PaziaDateRangeSelect
                    fromName={SESSIONS_FORM_NAME.SESSION_FROM}
                    toName={SESSIONS_FORM_NAME.SESSION_TO}
                    label="Data rozpoczęcia i zakończenia"
                    required
                  />

                  <div className="flex items-end justify-end">
                    <PaziaFormButton title="Dodaj" type="primary" />
                  </div>
                </div>
              </form>
            </PaziaBox>
            {sessions.length > 0 && (
              <div>
                <div className="flex flex-col gap-2">
                  {sessions.map((session) => {
                    return (
                      <PaziaBox key={uuidv4()}>
                        <div className="flex items-center justify-between">
                          <div className="flex flex-col">
                            <span className="font-semibold">{session.name}</span>
                            <span className="text-slate-500">
                              {formatDate(session.dateFrom)} - {formatDate(session.dateTo)}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <EditButton onClick={() => setEditSession(session)} />
                            <DeleteButton onClick={() => setSessionToDelete(session)} />
                          </div>
                        </div>
                      </PaziaBox>
                    );
                  })}
                </div>
              </div>
            )}
          </TabGrid>
        </TabContent>
      </FormProvider>
      <EditSessionModal
        session={editSession}
        sessions={sessions}
        onCloseRequest={async () => {
          setEditSession(undefined);
          await refreshSessions();
        }}
        courseId={courseId}
      />
      <ConfirmationModal
        isOpen={!!sessionToDelete}
        title="Usuń zjazd"
        description={`Czy na pewno chcesz usunąć zjazd ${sessionToDelete?.name ?? ""}?`}
        onCloseRequest={() => setSessionToDelete(undefined)}
        onCancel={() => setSessionToDelete(undefined)}
        onConfirm={async () => {
          await deleteSession(sessionToDelete!.id!);
          setSessionToDelete(undefined);
        }}
      />
    </>
  );
};

const SESSIONS_FORM_NAME = {
  SESSION_FROM: "sessionFrom",
  SESSION_TO: "sessionTo",
  NAME: "name",
  SESSIONS: "sessions",
} as const;

const sessionSchema = yup.object({
  name: yup.string().required(),
  dateFrom: yup.date().required(),
  dateTo: yup.date(),
  id: yup.number(),
});

const schema = yup
  .object({
    [SESSIONS_FORM_NAME.SESSION_FROM]: yup.date().required(),
    [SESSIONS_FORM_NAME.SESSION_TO]: yup
      .date()
      .required()
      .min(yup.ref(SESSIONS_FORM_NAME.SESSION_FROM), "Data zakończenia przed rozpoczęciem"),
    [SESSIONS_FORM_NAME.SESSIONS]: yup.array().of(sessionSchema).required(),
  })
  .required();

export type Session = yup.InferType<typeof sessionSchema>;
export type SessionFormValues = yup.InferType<typeof schema>;
